import React, { useEffect, useMemo, useState } from "react";
import { Button, Drawer, Space, Table, Typography, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { GetV2AutomationRuns, GetV2RunLogs, V2RunListItemDto, V2RunLogItemDto } from "../../api/v2/automations";
import V2Tag from "../../components/v2/V2Tag";
import Loader from "../../components/loader/Loader";

const { Text } = Typography;

type Props = {
    domain: string;
    automationId: number;
};

function StatusTag({ status }: { status: V2RunListItemDto["status"] }) {
    const color =
        status === "active" ? "green" :
            status === "error" ? "red" :
                status === "cancelled" ? "orange" :
                    status === "paused" ? "gold" :
                        "default";

    return <V2Tag color={color}>{status}</V2Tag>;
}

function prettyJson(v: any): string {
    try {
        if (v === null || v === undefined) return "";
        if (typeof v === "string") return v;
        return JSON.stringify(v, null, 2);
    } catch {
        return String(v);
    }
}

export default function RunsViewer({ domain, automationId }: Props) {
    const [loadingRuns, setLoadingRuns] = useState(true);
    const [runs, setRuns] = useState<V2RunListItemDto[]>([]);

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedRun, setSelectedRun] = useState<V2RunListItemDto | null>(null);

    const [loadingLogs, setLoadingLogs] = useState(false);
    const [logs, setLogs] = useState<V2RunLogItemDto[]>([]);

    const loadRuns = async () => {
        setLoadingRuns(true);
        try {
            const res = await GetV2AutomationRuns(domain, automationId, 50);
            if (!res.ok) {
                message.error(res.error || "Failed loading runs");
                setRuns([]);
                return;
            }
            setRuns(res.data?.runs || []);
        } finally {
            setLoadingRuns(false);
        }
    };

    useEffect(() => {
        loadRuns();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domain, automationId]);

    const openRun = async (run: V2RunListItemDto) => {
        setSelectedRun(run);
        setDrawerOpen(true);
        setLogs([]);
        setLoadingLogs(true);
        try {
            const res = await GetV2RunLogs(domain, run.id, 300);
            if (!res.ok) {
                message.error(res.error || "Failed loading run logs");
                return;
            }
            setLogs(res.data?.logs || []);
        } finally {
            setLoadingLogs(false);
        }
    };

    const columns: ColumnsType<V2RunListItemDto> = useMemo(() => [
        {
            title: "Run",
            dataIndex: "id",
            width: 90,
            render: (v) => <Text code>#{v}</Text>,
        },
        {
            title: "Status",
            dataIndex: "status",
            width: 120,
            render: (v) => <StatusTag status={v} />,
        },
        {
            title: "Trigger",
            dataIndex: "trigger_title",
            render: (_, r) => (
                <Space direction="vertical" size={0}>
                    <Text>{r.trigger_title || "-"}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        trigger_id: {r.trigger_id}
                    </Text>
                </Space>
            ),
        },
        {
            title: "Entered",
            dataIndex: "entered_at",
            width: 190,
        },
        {
            title: "Next due",
            dataIndex: "next_step_due_at",
            width: 190,
            render: (v) => v || <Text type="secondary">—</Text>,
        },
        {
            title: "Step",
            dataIndex: "current_step_index",
            width: 80,
            render: (v) => <Text>{v}</Text>,
        },
        {
            title: "",
            key: "actions",
            width: 110,
            render: (_, r) => (
                <Button onClick={() => openRun(r)}>Logs</Button>
            ),
        },
    ], [domain]);

    const logColumns: ColumnsType<V2RunLogItemDto> = [
        { title: "ts", dataIndex: "ts", width: 210 },
        { title: "type", dataIndex: "event_type", width: 180, render: (v) => <Text code>{v}</Text> },
        { title: "step", dataIndex: "step_index", width: 70, render: (v) => (v === null || v === undefined) ? "—" : v },
        {
            title: "payload / result",
            key: "json",
            render: (_, r) => (
                <pre style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: 12 }}>
{prettyJson({
    payload: r.payload_json ?? null,
    result: r.result_json ?? null,
})}
                </pre>
            ),
        },
    ];

    return (
        <div style={{ marginTop: 16 }}>
            <Space style={{ marginBottom: 8 }}>
                <Text strong>Runs</Text>
                <Button onClick={loadRuns} disabled={loadingRuns}>Refresh</Button>
            </Space>

            {loadingRuns ? (
                <div style={{ padding: 12 }}>
                    <Loader size={56} isFullPage={false} />
                </div>
            ) : (
                <Table
                    rowKey="id"
                    size="small"
                    columns={columns}
                    dataSource={runs}
                    pagination={false}
                />
            )}

            <Drawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                width={980}
                title={selectedRun ? `Run #${selectedRun.id} logs` : "Run logs"}
            >
                {selectedRun ? (
                    <Space direction="vertical" size="small" style={{ width: "100%" }}>
                        <Text type="secondary">
                            client_id: {selectedRun.client_id} | status: {selectedRun.status} | step: {selectedRun.current_step_index}
                        </Text>

                        {loadingLogs ? (
                            <Loader size={42} isFullPage={false} />
                        ) : (
                            <Table
                                rowKey="id"
                                size="small"
                                columns={logColumns}
                                dataSource={logs}
                                pagination={false}
                            />
                        )}
                    </Space>
                ) : null}
            </Drawer>
        </div>
    );
}
